import { useState } from "react";

import { useApiList } from "../api/hooks";
import { CrudPage } from "../components/CrudPage";
import { Bool, Money } from "../components/ui";
import { useConfig } from "../hooks/useConfig";

function useVendorTypeOptions() {
  const config = useConfig();
  return (config?.vendor_types ?? []).map((t) => ({ value: t.id, label: t.name }));
}

function useVendorOptions() {
  const list = useApiList<{ id: string; name: string }>("/admin/vendors", { per_page: 100 });
  return list.data.map((v) => ({ value: v.id, label: v.name }));
}

export function VendorTypesPage() {
  return (
    <CrudPage<{ id: string; name: string; slug: string; color: string | null; is_active: boolean; sort: number }>
      title="Vendor types"
      resource="vendor-types"
      paged={false}
      canDelete={false}
      columns={[
        { key: "name", label: "Name", render: (t) => (<><span className="dot" style={{ background: t.color ?? "#999" }} /> {t.name}</>) },
        { key: "slug", label: "Slug", render: (t) => <span className="mono">{t.slug}</span> },
        { key: "is_active", label: "Active", render: (t) => <Bool value={t.is_active} /> },
        { key: "sort", label: "Sort", align: "right" },
      ]}
      fields={[
        { name: "name", label: "Name", required: true },
        { name: "slug", label: "Slug", required: true, hint: "Lowercase, used in app config" },
        { name: "color", label: "Colour", type: "color" },
        { name: "description", label: "Description", type: "textarea" },
        { name: "logo_url", label: "Logo URL", type: "image" },
        { name: "feature_image_url", label: "Cover image URL", type: "image" },
        { name: "sort", label: "Sort", type: "number" },
        { name: "is_active", label: "Active", type: "switch" },
      ]}
    />
  );
}

export function CategoriesPage() {
  const types = useVendorTypeOptions();
  const [selected, setSelected] = useState<{ id: string; name: string } | null>(null);
  return (
    <>
      <CrudPage<{ id: string; name: string; vendor_type_id: string | null; is_active: boolean; sort: number; product_count: number }>
        title="Categories"
        resource="categories"
        searchable
        filters={[{ name: "vendor_type_id", label: "Vendor type", options: types }]}
        columns={[
          { key: "name", label: "Name" },
          { key: "vendor_type_id", label: "Vendor type", render: (c) => types.find((t) => t.value === c.vendor_type_id)?.label ?? "any" },
          { key: "product_count", label: "Products", align: "right" },
          { key: "is_active", label: "Active", render: (c) => <Bool value={c.is_active} /> },
          { key: "sort", label: "Sort", align: "right" },
        ]}
        fields={[
          { name: "name", label: "Name", required: true },
          { name: "vendor_type_id", label: "Vendor type", type: "select", options: types },
          { name: "image_url", label: "Image URL", type: "image" },
          { name: "sort", label: "Sort", type: "number" },
          { name: "is_active", label: "Active", type: "switch" },
        ]}
        rowActions={(c) => (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelected({ id: c.id, name: c.name })}>
            Subcategories
          </button>
        )}
      />
      {selected && (
        <section className="card" style={{ marginTop: 24 }}>
          <CrudPage<{ id: string; name: string; is_active: boolean; sort: number }>
            title={`Subcategories of ${selected.name}`}
            resource="subcategories"
            paged={false}
            listQuery={{ category_id: selected.id }}
            columns={[
              { key: "name", label: "Name" },
              { key: "is_active", label: "Active", render: (s) => <Bool value={s.is_active} /> },
              { key: "sort", label: "Sort", align: "right" },
            ]}
            fields={[
              { name: "name", label: "Name", required: true },
              { name: "image_url", label: "Image URL", type: "image" },
              { name: "sort", label: "Sort", type: "number" },
              { name: "is_active", label: "Active", type: "switch" },
            ]}
            toBody={(values, editing) => (editing ? values : { ...values, category_id: selected.id })}
          />
        </section>
      )}
    </>
  );
}

export function BannersPage() {
  const types = useVendorTypeOptions();
  const vendors = useVendorOptions();
  return (
    <CrudPage<{ id: string; image_url: string; link_url: string | null; vendor_type_id: string | null; vendor_id: string | null; is_active: boolean; sort: number }>
      title="Banners"
      resource="banners"
      paged={false}
      columns={[
        { key: "image_url", label: "Image", render: (b) => <img src={b.image_url} alt="" className="thumb" /> },
        { key: "vendor_type_id", label: "Vendor type", render: (b) => types.find((t) => t.value === b.vendor_type_id)?.label ?? "all" },
        { key: "vendor_id", label: "Links to vendor", render: (b) => vendors.find((v) => v.value === b.vendor_id)?.label ?? "—" },
        { key: "is_active", label: "Active", render: (b) => <Bool value={b.is_active} /> },
        { key: "sort", label: "Sort", align: "right" },
      ]}
      fields={[
        { name: "image_url", label: "Image URL", type: "image", required: true },
        { name: "link_url", label: "Link URL" },
        { name: "vendor_type_id", label: "Vendor type", type: "select", options: types },
        { name: "vendor_id", label: "Vendor", type: "select", options: vendors },
        { name: "sort", label: "Sort", type: "number" },
        { name: "is_active", label: "Active", type: "switch" },
      ]}
    />
  );
}

export function CouponsPage() {
  const vendors = useVendorOptions();
  return (
    <CrudPage<{
      id: string;
      code: string;
      description: string | null;
      discount: string;
      percentage: boolean;
      times: number;
      redemptions: number;
      redeemed_total: string;
      expires_on: string | null;
      is_active: boolean;
      vendor_ids: string[];
    }>
      title="Coupons"
      resource="coupons"
      searchable
      columns={[
        { key: "code", label: "Code", render: (c) => <span className="mono">{c.code}</span> },
        { key: "discount", label: "Discount", render: (c) => (c.percentage ? `${c.discount}%` : <Money value={c.discount} />) },
        { key: "times", label: "Max uses", align: "right", render: (c) => (c.times === 0 ? "∞" : c.times) },
        { key: "redemptions", label: "Redeemed", align: "right", render: (c) => (<>{c.redemptions} · <Money value={c.redeemed_total} /></>) },
        { key: "expires_on", label: "Expires", render: (c) => (c.expires_on ? c.expires_on.slice(0, 10) : "never") },
        { key: "vendor_ids", label: "Vendors", render: (c) => (c.vendor_ids.length === 0 ? "all" : c.vendor_ids.map((id) => vendors.find((v) => v.value === id)?.label ?? "?").join(", ")) },
        { key: "is_active", label: "Active", render: (c) => <Bool value={c.is_active} /> },
      ]}
      fields={[
        { name: "code", label: "Code", required: true, hint: "Customers type this at checkout; stored upper-case" },
        { name: "description", label: "Description" },
        { name: "discount", label: "Discount", type: "number", decimal: true, required: true },
        { name: "percentage", label: "Percentage (otherwise flat amount)", type: "switch" },
        { name: "times", label: "Max uses per customer (0 = unlimited)", type: "number" },
        { name: "expires_on", label: "Expires on", type: "date" },
        { name: "vendor_ids", label: "Limit to vendors", type: "multiselect", options: vendors },
        { name: "is_active", label: "Active", type: "switch" },
      ]}
      toForm={(c) => ({ ...c, expires_on: c.expires_on ? c.expires_on.slice(0, 10) : "" })}
      toBody={(values) => ({
        ...values,
        code: typeof values.code === "string" ? values.code.toUpperCase() : values.code,
        // The API stores a timestamp; a chosen day expires at its end (UTC).
        expires_on: typeof values.expires_on === "string" && values.expires_on ? `${values.expires_on}T23:59:59Z` : values.expires_on,
      })}
    />
  );
}

export function PackageTypesPage() {
  return (
    <CrudPage<{ id: string; name: string; description: string | null; is_active: boolean; sort: number }>
      title="Package types"
      resource="package-types"
      paged={false}
      canDelete={false}
      columns={[
        { key: "name", label: "Name" },
        { key: "description", label: "Description", render: (p) => p.description ?? "—" },
        { key: "is_active", label: "Active", render: (p) => <Bool value={p.is_active} /> },
        { key: "sort", label: "Sort", align: "right" },
      ]}
      fields={[
        { name: "name", label: "Name", required: true },
        { name: "description", label: "Description", type: "textarea" },
        { name: "image_url", label: "Image URL", type: "image" },
        { name: "sort", label: "Sort", type: "number" },
        { name: "is_active", label: "Active", type: "switch" },
      ]}
    />
  );
}

export function PaymentMethodsPage() {
  return (
    <CrudPage<{
      id: string;
      name: string;
      slug: string;
      kind: string;
      gateway: string | null;
      is_active: boolean;
      for_orders: boolean;
      for_topups: boolean;
      for_subscriptions: boolean;
      sort: number;
    }>
      title="Payment methods"
      resource="payment-methods"
      paged={false}
      canCreate={false}
      canDelete={false}
      columns={[
        { key: "name", label: "Name" },
        { key: "slug", label: "Slug", render: (m) => <span className="mono">{m.slug}</span> },
        { key: "kind", label: "Kind", render: (m) => (m.gateway ? `${m.kind} · ${m.gateway}` : m.kind) },
        { key: "for_orders", label: "Orders", render: (m) => <Bool value={m.for_orders} /> },
        { key: "for_topups", label: "Top-ups", render: (m) => <Bool value={m.for_topups} /> },
        { key: "for_subscriptions", label: "Subscriptions", render: (m) => <Bool value={m.for_subscriptions} /> },
        { key: "is_active", label: "Active", render: (m) => <Bool value={m.is_active} /> },
      ]}
      fields={[
        { name: "name", label: "Name" },
        { name: "instruction", label: "Instruction shown to customers", type: "textarea" },
        { name: "logo_url", label: "Logo URL", type: "image" },
        { name: "public_key", label: "Public key (gateways)", hint: "Secret keys come from the server environment, never from here" },
        { name: "for_orders", label: "Available for orders", type: "switch" },
        { name: "for_topups", label: "Available for wallet top-ups", type: "switch" },
        { name: "for_subscriptions", label: "Available for subscriptions", type: "switch" },
        { name: "sort", label: "Sort", type: "number" },
        { name: "is_active", label: "Active", type: "switch" },
      ]}
    />
  );
}

/** Vendor subscription plans (02 §14): vendors on the subscription model must hold a live one. */
export function SubscriptionPlansPage() {
  return (
    <CrudPage<{ id: string; name: string; days: number; amount: string; is_active: boolean }>
      title="Subscription plans"
      resource="subscription-plans"
      paged={false}
      canDelete={false}
      columns={[
        { key: "name", label: "Plan" },
        { key: "days", label: "Days", align: "right" },
        { key: "amount", label: "Amount", align: "right", render: (p) => <Money value={p.amount} /> },
        { key: "is_active", label: "Active", render: (p) => <Bool value={p.is_active} /> },
      ]}
      fields={[
        { name: "name", label: "Name", required: true },
        { name: "days", label: "Days", type: "number", required: true },
        { name: "amount", label: "Amount", type: "number", required: true },
        { name: "is_active", label: "Active", type: "switch", defaultValue: true },
      ]}
    />
  );
}
